import TextField from '@/components/TextField/TextField';
import { AGENT_STARTER_MESSAGE_MAX_LENGTH } from '@/pages/agents/constants';
import { useAgentEditorStore } from '@/state/root';
import theme from '@/theme';
import { QuestionsOperationType } from '@d-id/types';
import styled from '@emotion/styled';
import { InputAdornment } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

const CHAR_LIMIT = AGENT_STARTER_MESSAGE_MAX_LENGTH;
const inputProps = {
    maxLength: CHAR_LIMIT,
};

const QuestionForm = styled(TextField)`
    margin-right: 24px;

    & > div {
        box-sizing: content-box;

        height: 24px;
        padding: 8px;

        background-color: ${theme.colors.white};
        border-radius: 8px;
        border-color: ${theme.colors.primary.hover};

        color: ${theme.colors.secondary.main};
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 16px;
        letter-spacing: 0.15px;
    }

    input {
        text-overflow: ellipsis;
    }

    p {
        font-size: 14px;
        font-family: 'Manrope', sans-serif;
        font-weight: 600;
        color: gray;
        background-color: inherit !important;
    }

    .Mui-focused {
        background-color: ${theme.colors.primary.bgLight};

        input::placeholder {
            opacity: 0.5;
        }
    }

    .Mui-focused.Mui-error {
        background-color: ${theme.colors.error.bgLight};
    }

    & .MuiFormHelperText-root {
        width: 100%;
        margin-bottom: 8px;
        color: ${({ theme }) => theme.colors.error.main};
    }
`;

export const HelperTextContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;

    margin-inline: auto;
    margin-top: 4px;
`;

const AdornmentText = styled.text`
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0%;
`;

const EndAdornment = ({ textInput }: { textInput: string }) => (
    <InputAdornment position="end">
        <AdornmentText>
            {textInput.length}/{CHAR_LIMIT}
        </AdornmentText>
    </InputAdornment>
);

interface Props {
    operationType: QuestionsOperationType;
    question?: string;
    questions: string[];
    onSubmit: (value: { type: QuestionsOperationType; question: string; prevQuestion?: string }) => void;
    onDelete: (value: string) => void;
    onAddQuestion: () => void;
}

export const StarterQuestionForm = observer(({ question, onSubmit, onDelete, questions, operationType, onAddQuestion }: Props) => {
    const agentEditorStore = useAgentEditorStore();

    const [textInput, setTextInput] = useState(question || '');
    const [error, setError] = useState('');

    useEffect(() => {
        agentEditorStore.setIsStarterQuestionsEdited(false);
    }, []);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            onClickSubmit();
            onAddQuestion();
        }
    };

    const handleOnClose = () => {
        setTextInput('');
        agentEditorStore.setIsStarterQuestionsEdited(false);
    };

    const onInputChange = (e: any) => {
        agentEditorStore.setIsStarterQuestionsEdited(true);
        const value = e.target.value;

        if (questions.includes(value) && value !== question) {
            setError('Please change to a new message');
        } else if (value.trim().length < 3) {
            setError('Message needs to be at least 3 characters long');
        } else {
            setError('');
        }

        setTextInput(value);
    };

    function onClickSubmit() {
        agentEditorStore.setIsStarterQuestionsEdited(false);

        if (question && operationType === QuestionsOperationType.Edit && !textInput?.trim()) {
            onDelete(question);
            return;
        }

        if (operationType === QuestionsOperationType.Create && !textInput) {
            return;
        }

        if (!!error) {
            return;
        }

        if (question !== textInput) {
            onSubmit({
                type: operationType,
                question: textInput,
                prevQuestion: question,
            });

            handleOnClose();
        }
    }

    return (
        <QuestionForm
            color="primary"
            placeholder="Edit starter question"
            value={textInput}
            variant="outlined"
            fullWidth
            margin="none"
            InputLabelProps={{ shrink: false }}
            onChange={onInputChange}
            onKeyDown={handleKeyDown}
            onBlur={onClickSubmit}
            error={!!error}
            helperText={error && <HelperTextContainer>{error}</HelperTextContainer>}
            inputProps={inputProps}
            InputProps={{ endAdornment: textInput.length > CHAR_LIMIT - 5 && <EndAdornment textInput={textInput} /> }}
        />
    );
});
